import { useEffect, useRef, useState } from 'react'
import { AlertTriangle, RefreshCw, Loader2 } from 'lucide-react'
import { useLivekitStore } from '../../stores/livekitStore'
import { useVoiceStore } from '../../stores/voiceStore'
import { useAuthStore } from '../../stores/authStore'
import { mediaManager } from '../../services/mediaManager'

// How long the mic must be CONTINUOUSLY stuck (unmuted-intent but no live audio
// track) before we escalate the passive "connecting" spinner into an actionable
// Reconnect prompt. The normal publish/denoiser-attach window is ~2–3s, so 6s is
// safely past it — we only surface for a genuine stall, not a routine join.
const STUCK_MS = 6000
// Re-check cadence. Cheap reads; 1s matches the mediaManager health-check tick.
const POLL_MS = 1000

/**
 * "Microphone may not be transmitting" escalation (2026-06-29).
 *
 * The mic button already shows a passive yellow spinner while the local audio
 * track isn't live (`showMicWarning`). But on the 25-person standup that day
 * several people (Konstantin ×6, Igor) had a mic that never started publishing
 * and their ONLY recourse was to refresh the whole page (losing 10–30s + a fresh
 * ICE negotiation). This turns that blind full-page refresh into a targeted,
 * in-place recovery: when the mic has been stuck for >STUCK_MS we show a
 * Reconnect button that re-publishes the mic (room up) or rejoins the voice
 * channel in place (room gone) — no page reload.
 *
 * Trigger is deliberately NARROW to avoid DTX / benign false-positives:
 *   • intent = unmuted (persisted `settings.isMuted === false`), AND
 *   • `isAudioTrackReady === false` (the audio track is genuinely not live), AND
 *   • sustained continuously for STUCK_MS.
 * We do NOT trigger on `denoiser === 'idle'` (that's the common per-room state and
 * is benign — it was the cause of the historic perpetual-spinner false alarm).
 */
export default function MicStuckBanner() {
  const room = useLivekitStore((s) => s.room)
  const isAudioTrackReady = useLivekitStore((s) => s.isAudioTrackReady)
  const intentMuted = useAuthStore((s) => !!s.user?.settings?.isMuted)
  const currentChannelId = useVoiceStore((s) => s.currentChannelId)

  const [stuck, setStuck] = useState(false)
  const [busy, setBusy] = useState(false)
  // When the bad condition first became true (null = currently healthy).
  const badSinceRef = useRef<number | null>(null)

  const inVoice = !!room || !!currentChannelId
  // "Should be transmitting but isn't": unmuted intent + no live audio track.
  const bad = inVoice && !intentMuted && isAudioTrackReady === false

  useEffect(() => {
    if (!bad) {
      badSinceRef.current = null
      if (stuck) setStuck(false)
      return
    }
    if (badSinceRef.current == null) badSinceRef.current = Date.now()
    const id = setInterval(() => {
      const since = badSinceRef.current
      if (since != null && Date.now() - since >= STUCK_MS) setStuck(true)
    }, POLL_MS)
    return () => clearInterval(id)
  }, [bad, stuck])

  if (!stuck) return null

  const reconnect = async () => {
    if (busy) return
    setBusy(true)
    try {
      const liveRoom = useLivekitStore.getState().room
      if (liveRoom) {
        // Room is up (possibly mid-reconnect) — re-publish the mic in place. This
        // is exactly the path the health-check loop uses to recover a desync, so
        // it's safe and chained through mediaManager's single-flight mic queue.
        await mediaManager.setMicrophoneEnabled(liveRoom, true)
      } else {
        // Fully disconnected — rejoin the voice channel in place instead of a
        // full browser refresh. Mirrors VoiceManager's auto-reconnect args.
        const cid = useVoiceStore.getState().currentChannelId
        const { audioInputDeviceId, isNoiseSuppressionOn } = useVoiceStore.getState()
        const user = useAuthStore.getState().user
        const username = user?.username || user?.id || 'User'
        if (cid && user?.id) {
          await useLivekitStore
            .getState()
            .connect(cid, username, audioInputDeviceId, isNoiseSuppressionOn, { silentIfBlocked: true })
        }
      }
      // Give the re-publish/rejoin a moment to settle before re-arming the timer,
      // so a successful recovery dismisses the banner instead of instantly
      // re-showing it on the next poll.
      badSinceRef.current = null
      setStuck(false)
    } catch (err) {
      console.warn('[MicStuckBanner] reconnect failed', err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="w-full flex items-center justify-center pb-1">
      <div className="flex items-center gap-2 rounded-full bg-yellow-500/15 px-3 py-1 text-sm text-yellow-600 dark:text-yellow-400">
        <AlertTriangle size={15} className="shrink-0" />
        <span>Your microphone may not be transmitting.</span>
        <button
          type="button"
          onClick={reconnect}
          disabled={busy}
          className="ml-1 inline-flex items-center gap-1 rounded-full bg-yellow-500 px-2.5 py-0.5 text-xs font-medium text-white transition hover:bg-yellow-600 disabled:opacity-60"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          Reconnect
        </button>
      </div>
    </div>
  )
}
